import React, { useEffect, useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Card,
  Select,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Input
} from 'antd';
import {
  FileExcelOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BookOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

const TutorClassLog = () => {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total) => `${t('Total')}: ${total} ${t('classes')}`,
  });

  // Helper function to format date as YYYY-MM-DD
  const formatDateForInput = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Default to today's date
  const today = formatDateForInput(new Date());
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [statusFilter, setStatusFilter] = useState(null);
  const [adminapproveFilter, setAdminapproveFilter] = useState(null);
  const [tutorFilter, setTutorFilter] = useState(null);
  const [summaryStats, setSummaryStats] = useState({
    totalClasses: 0,
    confirmedClasses: 0,
    pendingClasses: 0,
    adminConfirmed: 0
  });

  const [prevFilters, setPrevFilters] = useState({
    startDate: today,
    endDate: today,
    statusFilter: null,
    adminapproveFilter: null,
    tutorFilter: null
  });

  useEffect(() => {
    fetchClasses();
  }, [startDate, endDate]);

  const fetchClasses = async () => {
    if (!startDate || !endDate) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/classes/by-month?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClasses(response.data);
      setFilteredClasses(response.data);
      setLoading(false);
      calculateSummaryStats(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
      message.error(t('Failed to fetch classes'));
      setLoading(false);
    }
  };

  const calculateSummaryStats = (data) => {
    const stats = {
      totalClasses: data.length,
      confirmedClasses: data.filter(cls => cls.status === 'approved').length,
      pendingClasses: data.filter(cls => cls.status !== 'approved').length,
      adminConfirmed: data.filter(cls => cls.adminapprove === 'adminconfirm').length
    };
    setSummaryStats(stats);
  };

  const handleAdminApprove = async (classId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${process.env.REACT_APP_API_URL}/classes/${classId}/adminapprove`, {
        adminapprove: status
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setClasses(prevClasses => prevClasses.map(cls => 
        cls.classid === classId ? { ...cls, adminapprove: status } : cls
      ));
      
      setFilteredClasses(prevFilteredClasses => prevFilteredClasses.map(cls => 
        cls.classid === classId ? { ...cls, adminapprove: status } : cls
      ));
      
      setSummaryStats(prevStats => ({
        ...prevStats,
        adminConfirmed: status === 'adminconfirm' 
          ? prevStats.adminConfirmed + 1 
          : prevStats.adminConfirmed - 1
      }));
      
      message.success(t('Class approved successfully'));
    } catch (error) {
      console.error('Error confirming hours:', error);
      message.error(t('Failed to approve class'));
    }
  };

  const handleAdminUnapprove = async (classid) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${process.env.REACT_APP_API_URL}/classes/${classid}/admin-unapprove`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setClasses(prevClasses => prevClasses.map(cls => 
        cls.classid === classid ? { ...cls, adminapprove: 'pending' } : cls
      ));
      
      setFilteredClasses(prevFilteredClasses => prevFilteredClasses.map(cls => 
        cls.classid === classid ? { ...cls, adminapprove: 'pending' } : cls
      ));
      
      setSummaryStats(prevStats => ({
        ...prevStats,
        adminConfirmed: prevStats.adminConfirmed - 1
      }));
      
      message.success(t('Admin approval revoked successfully'));
    } catch (error) {
      console.error('Error revoking admin approval:', error);
      message.error(t('Failed to revoke admin approval'));
    }
  };

  useEffect(() => {
    const applyFilters = () => {
      const filtered = classes.filter((cls) => {
        const isStatusMatch = statusFilter ? cls.status === statusFilter : true;
        const isAdminapproveMatch = adminapproveFilter ? cls.adminapprove === adminapproveFilter : true;
        const isTutorMatch = tutorFilter ? cls.nickname === tutorFilter : true;
        
        return isStatusMatch && isAdminapproveMatch && isTutorMatch;
      }).sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        if (dateA !== dateB) return dateA - dateB;

        const timeA = new Date(`1970-01-01T${a.schedule_time}`).getTime();
        const timeB = new Date(`1970-01-01T${b.schedule_time}`).getTime();
        return timeA - timeB;
      });
      
      setFilteredClasses(filtered);
      
      const filtersChanged = 
        startDate !== prevFilters.startDate || 
        endDate !== prevFilters.endDate || 
        statusFilter !== prevFilters.statusFilter || 
        adminapproveFilter !== prevFilters.adminapproveFilter || 
        tutorFilter !== prevFilters.tutorFilter;
      
      if (filtersChanged) {
        setPagination(prev => ({
          ...prev,
          current: 1,
          total: filtered.length
        }));
        
        setPrevFilters({
          startDate,
          endDate,
          statusFilter,
          adminapproveFilter,
          tutorFilter
        });
      } else {
        setPagination(prev => ({
          ...prev,
          total: filtered.length
        }));
      }
      
      calculateSummaryStats(filtered);
    };
    
    applyFilters();
  }, [statusFilter, adminapproveFilter, tutorFilter, classes, startDate, endDate, prevFilters]);

  const getUniqueTutors = () => {
    const tutors = classes.map(cls => cls.nickname);
    return [...new Set(tutors)].sort();
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const csvData = filteredClasses.map(cls => ({
    No: cls.classid,
    Date: new Date(cls.date).toLocaleDateString(),
    ClassCode: cls.class_code,
    ClassID: cls.classid,
    Subject: cls.subject_name,
    Teacher: `${cls.nickname} (${cls.teacher_name})`,
    Time: formatTime(cls.schedule_time),
    ScheduleHour: cls.schedule_hour,
    Status: cls.status === 'approved' ? t('confirmed') : t('confirmpending'),
    AdminApprove: cls.adminapprove === 'adminconfirm' ? t('Admin Confirmed') : t('Pending')
  }));

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '';

    const [startHour, startMinute] = startTime.split(':').map(Number);

    const match = duration.match(/(\d+)h\s*(\d+)?m?/);
    let hours = 0, minutes = 0;

    if (match) {
      hours = parseInt(match[1], 10);
      minutes = parseInt(match[2] || '0', 10);
    }

    let endHour = startHour + hours;
    let endMinute = startMinute + minutes;

    if (endMinute >= 60) {
      endHour += Math.floor(endMinute / 60);
      endMinute %= 60;
    }

    return `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
  };

  // Quick date range preset functions
  const setToday = () => {
    const today = formatDateForInput(new Date());
    setStartDate(today);
    setEndDate(today);
  };

  const setYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = formatDateForInput(yesterday);
    setStartDate(dateStr);
    setEndDate(dateStr);
  };

  const setThisWeek = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    setStartDate(formatDateForInput(startOfWeek));
    setEndDate(formatDateForInput(endOfWeek));
  };

  const setLastWeek = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfLastWeek = new Date(now);
    startOfLastWeek.setDate(now.getDate() - dayOfWeek - 7);
    const endOfLastWeek = new Date(startOfLastWeek);
    endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
    
    setStartDate(formatDateForInput(startOfLastWeek));
    setEndDate(formatDateForInput(endOfLastWeek));
  };

  const setThisMonth = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(formatDateForInput(startOfMonth));
    setEndDate(formatDateForInput(endOfMonth));
  };

  const setLastMonth = () => {
    const now = new Date();
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    
    setStartDate(formatDateForInput(startOfLastMonth));
    setEndDate(formatDateForInput(endOfLastMonth));
  };

  const columns = [
    {
      title: t('No'),
      dataIndex: 'classid',
      key: 'no',
      width: 70,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => (
        <Space>
          <CalendarOutlined />
          <Text>{formatDateDisplay(date)}</Text>
        </Space>
      ),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: t('classcode'),
      dataIndex: 'class_code',
      key: 'classcode',
      width: 120,
      render: (code) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: t('classid'),
      dataIndex: 'classid',
      key: 'classid',
      width: 80,
    },
    {
      title: t('subject'),
      dataIndex: 'subject_name',
      key: 'subject',
      width: 150,
      render: (subject) => (
        <Tooltip title={subject}>
          <Space>
            <BookOutlined />
            <Text ellipsis style={{ maxWidth: 120 }}>{subject}</Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: t('teacher'),
      dataIndex: 'nickname',
      key: 'teacher',
      width: 120,
      render: (nickname) => (
        <Space>
          <UserOutlined />
          <Text>{nickname}</Text>
        </Space>
      ),
      filters: getUniqueTutors().map(tutor => ({ text: tutor, value: tutor })),
      onFilter: (value, record) => record.nickname === value,
    },
    {
      title: t('time'),
      dataIndex: 'schedule_time',
      key: 'time',
      width: 120,
      render: (time, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            <ClockCircleOutlined /> {formatTime(time)} - {calculateEndTime(time, record.schedule_hour)}
          </Text>
        </Space>
      ),
    },
    {
      title: t('Hour'),
      dataIndex: 'schedule_hour',
      key: 'hour',
      width: 80,
      render: (hours) => <Tag color="purple">{hours}</Tag>,
    },
    {
      title: t('confirmteach'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        return status === 'approved' ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t('HOURSAPPROVED')}
          </Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning">
            {t('confirmpending')}
          </Tag>
        );
      },
      filters: [
        { text: t('confirmed'), value: 'approved' },
        { text: t('confirmpending'), value: 'pending' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: t('adminapprove'),
      dataIndex: 'adminapprove',
      key: 'adminapprove',
      width: 200,
      render: (status, record) => {
        if (status === 'adminconfirm') {
          return (
            <Space>
              <Tag icon={<CheckCircleOutlined />} color="success">
                {t('Admin Confirmed')}
              </Tag>
              <Popconfirm
                title={t('Revoke Admin Approval')}
                description={t('Are you sure you want to revoke admin approval for this class?')}
                okText={t('Yes')}
                cancelText={t('No')}
                okType="danger"
                onConfirm={() => handleAdminUnapprove(record.classid)}
              >
                <Button
                  danger
                  size="small"
                  shape="circle"
                  icon={<CloseOutlined />}
                  title={t('Unconfirm')}
                />
              </Popconfirm>
            </Space>
          );
        } else {
          return (
            <Popconfirm
              title={t('Confirm Admin Approval')}
              description={t('Are you sure you want to confirm this class?')}
              onConfirm={() => handleAdminApprove(record.classid, 'adminconfirm')}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <Button 
                type="primary" 
                size="small" 
                icon={<CheckOutlined />}
              >
                {t('Admin Confirm')}
              </Button>
            </Popconfirm>
          );
        }
      },
      filters: [
        { text: t('Admin Confirmed'), value: 'adminconfirm' },
        { text: t('Pending'), value: 'pending' },
      ],
      onFilter: (value, record) => record.adminapprove === value,
    },
  ];

  return (
    <div>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Total Classes')}
              value={summaryStats.totalClasses}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Confirmed Classes')}
              value={summaryStats.confirmedClasses}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Pending Classes')}
              value={summaryStats.pendingClasses}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('Admin Confirmed')}
              value={summaryStats.adminConfirmed}
              valueStyle={{ color: '#722ed1' }}
              prefix={<CheckOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Export */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Date Range Section */}
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Space wrap size="small">
                <Text strong><CalendarOutlined /> {t('Date Range')}:</Text>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: 150 }}
                />
                <Text>-</Text>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: 150 }}
                />
                <Button size="small" onClick={setToday}>{t('Today')}</Button>
                <Button size="small" onClick={setYesterday}>{t('Yesterday')}</Button>
                <Button size="small" onClick={setThisWeek}>{t('This Week')}</Button>
                <Button size="small" onClick={setLastWeek}>{t('Last Week')}</Button>
                <Button size="small" onClick={setThisMonth}>{t('This Month')}</Button>
                <Button size="small" onClick={setLastMonth}>{t('Last Month')}</Button>
              </Space>
            </Col>
          </Row>

          {/* Filters and Export Section */}
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={18}>
              <Space wrap size="middle">
                <Space>
                  <Text strong><UserOutlined /> {t('Tutor')}:</Text>
                  <Select
                    style={{ width: 150 }}
                    value={tutorFilter}
                    onChange={setTutorFilter}
                    placeholder={t('Select Tutor')}
                    allowClear
                    showSearch
                  >
                    <Option value="">{t('All Tutors')}</Option>
                    {getUniqueTutors().map(tutor => (
                      <Option key={tutor} value={tutor}>
                        {tutor}
                      </Option>
                    ))}
                  </Select>
                </Space>

                <Space>
                  <Text strong><CheckCircleOutlined /> {t('Status')}:</Text>
                  <Select
                    style={{ width: 150 }}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder={t('Select Status')}
                    allowClear
                  >
                    <Option value="">{t('All Statuses')}</Option>
                    <Option value="approved">{t('confirmed')}</Option>
                    <Option value="pending">{t('confirmpending')}</Option>
                  </Select>
                </Space>

                <Space>
                  <Text strong><CheckOutlined /> {t('Admin Status')}:</Text>
                  <Select
                    style={{ width: 180 }}
                    value={adminapproveFilter}
                    onChange={setAdminapproveFilter}
                    placeholder={t('Select Admin Status')}
                    allowClear
                  >
                    <Option value="">{t('All Statuses')}</Option>
                    <Option value="adminconfirm">{t('Admin Confirmed')}</Option>
                    <Option value="pending">{t('Pending')}</Option>
                  </Select>
                </Space>
              </Space>
            </Col>
            
            <Col xs={24} sm={24} md={6} style={{ textAlign: 'right' }}>
              <CSVLink 
                data={csvData} 
                filename={`tutor_class_log_${startDate}_to_${endDate}.csv`}
                style={{ textDecoration: 'none' }}
              >
                <Button 
                  type="primary" 
                  icon={<FileExcelOutlined />}
                >
                  {t('exportToCSV')}
                </Button>
              </CSVLink>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Table with hover effect */}
      <Card>
        <style>
          {`
            .ant-table-tbody > tr:hover > td {
              background-color: #ffebee !important;
              cursor: pointer;
            }
          `}
        </style>
        <Table
          columns={columns}
          dataSource={filteredClasses}
          rowKey="classid"
          pagination={pagination}
          onChange={handleTableChange}
          loading={loading}
          scroll={{ x: 'max-content' }}
          size="middle"
          bordered
        />
      </Card>
    </div>
  );
};

export default withRoleAccess(TutorClassLog, ['superadmin', 'admin']);